import type { ProcessedSlot } from '../types/timeSlots';
import { filterSlotMenusByArea } from './areaUtils';

export interface AreaContext {
  isAreaMode?: boolean;
  /** string | number: menu-derived area ids are always string; numeric ids may appear from API responses. */
  selectedArea?: { id: string | number; name: string } | null;
}

/**
 * Same-day times (HH:mm) where `menuId` is bookable (present and not disabled).
 * Pure: derives from the already-fetched day slots — no API call.
 */
export function findAlternativeTimesForMenu(
  timeSlots: ProcessedSlot[] | null | undefined,
  menuId: number,
  ctx: AreaContext = {},
): string[] {
  if (!timeSlots) return [];
  const times: string[] = [];
  for (const slot of timeSlots) {
    const menus =
      ctx.isAreaMode && ctx.selectedArea
        ? filterSlotMenusByArea(slot.menus, ctx.selectedArea)
        : slot.menus;
    if (menus.some((m) => m.id === menuId && !m.disabled)) {
      times.push(slot.hour);
    }
  }
  return times;
}

export interface SwitchTimeSelection {
  time: string;
  experience: { id: string; title: string };
  area?: { id: string; name: string };
}

/**
 * The booking selection to apply when the user taps `newTime` on an unavailable
 * experience card. Returns null if the menu isn't actually enabled in that slot.
 */
export function resolveSwitchTimeSelection(
  timeSlots: ProcessedSlot[] | null | undefined,
  menuId: number,
  newTime: string,
  ctx: AreaContext = {},
): SwitchTimeSelection | null {
  const slot = timeSlots?.find((s) => s.hour === newTime);
  const menu = slot?.menus.find((m) => m.id === menuId && !m.disabled);
  if (!menu) return null;
  const selection: SwitchTimeSelection = {
    time: newTime,
    experience: { id: String(menu.id), title: menu.name },
  };
  // Mirrors ExperienceSelector.handleSelect: area is menu-derived only in menu mode.
  if (!ctx.isAreaMode && menu.area) {
    selection.area = { id: menu.area, name: menu.area };
  }
  return selection;
}
